/** What a holder is told when connecting Phantom fails, and why. */

export const NOT_INSTALLED =
  "Install Phantom from the tab we just opened, then reload this page and connect.";
export const PHANTOM_DOWNLOAD = "https://phantom.com/download";
/* Brave's own wallet claims `window.solana` by default and can keep Phantom
   from registering, so a holder with Phantom installed was told to install it. */
export const BRAVE_WALLET_FIRST =
  "Install Phantom from the tab we just opened. If you already have it, Brave may be hiding it: set Brave Settings > Wallet > Default Solana wallet to Extensions (no fallback), then reload this page.";

/* Phantom answers both signIn and connect with a bare "Unexpected error"
   within milliseconds when it cannot serve any request: installed but not
   set up yet, or locked (Faro, 2026-10-18: ~5 an hour, often right after the
   download page). The bare text gave the holder nothing to do. */
/* After a declined prompt Phantom can answer every later request in the tab
   with the same bare error until the page reloads (one holder, 15 presses,
   2026-10-25), so the copy ends on a reload. */
export const PHANTOM_NOT_READY =
  "Phantom could not connect. Open the Phantom extension, finish setting it up or unlock it, then press Connect again. If it still fails, reload this page.";
/* The same failure inside Phantom's own app browser on a phone, where there is
   no extension to open: one iOS holder pressed Connect 15 times against the
   desktop wording (Faro, 2026-10-19). */
export const PHANTOM_NOT_READY_PHONE =
  "Phantom could not connect. Unlock Phantom and check a Solana account is selected, then reload this page and press Connect again.";
export const DECLINED =
  "Sign-in was declined, so nothing was connected. Press Connect again to connect without signing in.";

/* MetaMask's Solana accounts, through the Wallet Standard, answered three ways
   that reached holders as raw text or nothing at all (Faro, 2026-10-25: 41
   failed connects for 7 holders in six hours). Each names what to do. */
/** A request from an earlier press, never answered, still holds the wallet. */
export const walletPending = (name: string) =>
  `${name} already has a connection request open. Open ${name}, approve or reject it, then press Connect again.`;
/** The extension's background worker went away mid-request. */
export const walletAsleep = (name: string) =>
  `${name} stopped responding. Reload this page, then press Connect again.`;
/** The wallet connected but offered no Solana account. */
export const walletNoAccount = (name: string) =>
  `${name} has no Solana account to connect. Add or select a Solana account in ${name}, then press Connect again.`;

/** The holder's next step for a wallet's own refusal, or undefined to keep it. */
export function walletRefusal(error: unknown, name: string): Error | undefined {
  if (!(error instanceof Error)) return undefined;
  // Ronin says `already_exist` for a pending request, and "Extension context
  // invalidated" once it reloads under the page (one holder, 2026-11-03).
  const copy = /already pending|^already_exist$/i.test(error.message)
    ? walletPending
    : /port not connected|extension context invalidated/i.test(error.message)
      ? walletAsleep
      : error.name === "WalletAccountError"
        ? walletNoAccount
        : undefined;
  return copy && new Error(copy(name), { cause: error });
}
